import React, { useEffect, useState, useCallback } from 'react';
import JsonLd from '../components/JsonLd';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  BarChart3,
  Bot,
  Brain,
  CheckCircle2,
  Database,
  Layout,
  LineChart,
  MessageSquare,
  Mic,
  PieChart,
  RefreshCw,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sections = [
  { id: 'opening', label: 'Overview' },
  { id: 'features', label: 'Features' },
  { id: 'benefits', label: 'Benefits' },
];

const AnalyticsChatAgentsPage = () => {
  const [activeSection, setActiveSection] = useState('opening');

  const handleScroll = useCallback(() => {
    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return { id, top: Infinity };
      return { id, top: el.getBoundingClientRect().top };
    });
    let current = offsets[0]?.id;
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const capabilities = [
    'Connect to your data warehouse seamlessly',
    'Query your data using natural voice or chat',
    'Generate dynamic charts from query results',
    'Create custom dashboards with pinned insights',
    'Auto-refresh dashboards daily',
    'Choose from multiple chart types and visualizations',
  ];

  const features = [
    {
      icon: Database,
      title: 'Data Warehouse Integration',
      desc: 'Connect directly to your data warehouse for real-time analysis',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Mic,
      title: 'Voice & Chat Interface',
      desc: 'Ask questions in natural language via voice or chat',
      gradient: 'from-cyan-500 to-teal-500',
    },
    {
      icon: LineChart,
      title: 'Smart Visualization',
      desc: 'Automatically generate charts from your queries',
      gradient: 'from-teal-500 to-emerald-500',
    },
    {
      icon: Layout,
      title: 'Dashboard Builder',
      desc: 'Pin insights and build custom dashboards',
      gradient: 'from-emerald-500 to-green-500',
    },
  ];

  const benefits = [
    { icon: Database, text: 'Direct data warehouse connectivity' },
    { icon: MessageSquare, text: 'Natural language queries via voice or chat' },
    { icon: BarChart3, text: 'Dynamic chart generation' },
    { icon: Layout, text: 'Custom dashboard creation' },
    { icon: RefreshCw, text: 'Daily auto-refresh capabilities' },
    { icon: PieChart, text: 'Multiple visualization options' },
  ];

  return (
    <div className="min-h-screen bg-white relative">
      <SEOHead page="analyticsChatAgents" />
      <JsonLd schema={{"@context":"https://schema.org","@graph":[{"@type":"SoftwareApplication","name":"Digian Analytics Chat Agents","applicationCategory":"BusinessApplication","operatingSystem":"Cloud","description":"Query your business data in plain English with Digian's AI-powered analytics chat agents. Instant insights, no SQL required.","url":"https://digian.ai/analytics-chat-agents","offers":{"@type":"Offer","seller":{"@type":"Organization","name":"Digian"}}},{"@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Which data warehouses do you support?","acceptedAnswer":{"@type":"Answer","text":"Digian.ai Analytics connects to Snowflake, Databricks, BigQuery, Redshift, Azure Synapse, and more. Custom connectors are available for enterprise plans."}},{"@type":"Question","name":"Do users need SQL knowledge?","acceptedAnswer":{"@type":"Answer","text":"No. The natural language interface is designed for non-technical business users. SQL is generated automatically from plain-English or voice queries."}},{"@type":"Question","name":"How does dashboard auto-refresh work?","acceptedAnswer":{"@type":"Answer","text":"Dashboards can be configured to refresh on a daily schedule or on-demand. Refresh frequency is configurable per dashboard on Professional and Enterprise plans."}},{"@type":"Question","name":"Is my data stored by Digian.ai?","acceptedAnswer":{"@type":"Answer","text":"No. Digian.ai queries your data warehouse directly and does not store raw data. Query results may be cached temporarily for performance. See our Privacy Policy for details."}}]}]}} />
      {/* Sticky Timeline Sidebar (desktop only) */}
      <nav className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
        <div className="relative flex flex-col items-start pl-1.5">
          {sections.map((sec, i) => {
            const isActive = activeSection === sec.id;
            const activeIdx = sections.findIndex(s => s.id === activeSection);
            const isPast = i < activeIdx;
            return (
              <button
                key={sec.id}
                onClick={() => scrollTo(sec.id)}
                className="relative flex items-center gap-3 group py-2"
              >
                <div
                  className={`relative z-10 w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isActive
                      ? 'bg-blue-600 border-blue-600 scale-125'
                      : isPast
                      ? 'bg-blue-400 border-blue-400'
                      : 'bg-white border-slate-300 group-hover:border-blue-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                    isActive
                      ? 'text-blue-600 font-semibold'
                      : isPast
                      ? 'text-blue-400'
                      : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Opening Section */}
      <section id="opening" className="relative overflow-hidden bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-teal-500/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              Talk to Your Data with{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-600">
                Voice & Chat Analytics
              </span>
            </h1>
            <p className="text-xl text-blue-600 font-medium">
              Connect to your data warehouse and get instant insights through natural conversation
            </p>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-blue-50 border-l-4 border-blue-600 rounded-r-xl p-6">
                  <p className="text-slate-800 leading-relaxed">
                    <strong className="text-blue-600">digian.ai Analytics & Chat Agents</strong> transforms how you interact with your data warehouse. Ask questions in natural language using voice or chat, visualize results in dynamic charts, and build custom dashboards that refresh automatically.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Bot className="w-6 h-6 text-blue-600" /> Key Capabilities:
                  </h3>
                  <div className="space-y-3">
                    {capabilities.map((cap, i) => (
                      <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-3 p-3 bg-white rounded-xl hover:translate-x-1 transition-transform hover:shadow-md">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-800">{cap}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              <motion.div {...fadeUp} className="lg:col-span-2 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl p-8 text-white shadow-lg self-start">
                <h3 className="text-2xl font-bold flex items-center gap-2 mb-6">
                  <Zap className="w-6 h-6" /> IMPACT
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-blue-200 mb-2">GOAL</h4>
                    <p className="text-lg">Make data analysis accessible to everyone through conversation</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-blue-200 mb-2">OUTCOME</h4>
                    <p className="text-lg">Faster insights, better decisions, self-service analytics</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Product <span className="text-blue-600">Features</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Powerful features for conversational data analytics</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-gradient-to-br ${f.gradient} rounded-xl p-6 text-white shadow-lg`}>
                  <Icon className="w-10 h-10 mb-3" />
                  <h4 className="text-lg font-bold mb-1">{f.title}</h4>
                  <p className="text-sm text-white/90">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Business <span className="text-blue-600">Benefits</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Transform how your organization accesses and uses data</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-4 p-5 bg-white rounded-xl border-l-4 border-blue-500">
                  <Icon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-800 font-medium">{b.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: 'Which data warehouses do you support?', a: 'Digian.ai Analytics connects to Snowflake, Databricks, BigQuery, Redshift, Azure Synapse, and more. Custom connectors are available for enterprise plans.' },
              { q: 'Do users need SQL knowledge?', a: 'No. The natural language interface is designed for non-technical business users. SQL is generated automatically from plain-English or voice queries.' },
              { q: 'How does dashboard auto-refresh work?', a: 'Dashboards can be configured to refresh on a daily schedule or on-demand. Refresh frequency is configurable per dashboard on Professional and Enterprise plans.' },
              { q: 'Is my data stored by Digian.ai?', a: 'No. Digian.ai queries your data warehouse directly and does not store raw data. Query results may be cached temporarily for performance. See our Privacy Policy for details.' },
            ].map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="bg-gradient-to-r from-blue-600 to-cyan-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">
              Ready to Talk to Your <span className="text-blue-200">Data?</span>
            </h2>
            <p className="text-xl text-blue-100">
              Start getting insights from your data warehouse through natural conversation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Request Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AnalyticsChatAgentsPage;
